// @flow

import * as React from 'react';
import { RootContainer } from 'react-relay/classic';

import JobComponent from 'app/components/job/Job';

import * as BuildQuery from 'app/queries/Build';
import AnnotationsList from './AnnotationsList';
import Header from '../Header';
import ManualJobSummary, { type ManualStepType } from './ManualJobSummary';
import TriggerJobSummary, { type TriggerStepType } from './TriggerJobSummary';
import WaiterJobSummary, { type WaiterStepType } from './WaiterJobSummary';

type Props = {
  store: {
    on: Function,
    off: Function,
    getBuild: Function
  }
};

type ScriptStepType = {
  type: 'script',
  id: string,
  retriedInJobUuid: ?string
};

type Step = ScriptStepType | ManualStepType | TriggerStepType | WaiterStepType;

type State = {
  build: {
    number: number,
    account: {
      slug: string
    },
    project: {
      slug: string
    },
    jobs: Array<Step>
  }
};

export default class BuildShow extends React.PureComponent<Props, State> {
  constructor(initialProps: Props) {
    super(initialProps);

    this.state = {
      build: initialProps.store.getBuild()
    };

    // `this.props.store` is an EventEmitter which always
    // calls a given event handler in its own context 🤦🏻‍♀️
    // so let's override that!
    this.handleStoreChange = this.handleStoreChange.bind(this);
  }

  componentDidMount() {
    this.props.store.on('change', this.handleStoreChange);
  }

  componentWillUnmount() {
    this.props.store.off('change', this.handleStoreChange);
  }

  handleStoreChange = () => {
    this.setState({
      build: this.props.store.getBuild()
    });
  };

  render() {
    const build = this.state.build;

    return (
      <div>
        <Header
          build={build}
          showRebuild={true}
        />
        <RootContainer
          Component={AnnotationsList}
          route={{
            name: 'AnnotationsRoute',
            queries: {
              build: BuildQuery.query
            },
            params: BuildQuery.prepareParams({
              organization: build.account.slug,
              pipeline: build.project.slug,
              number: build.number
            })
          }}
        />
        {this.renderJobList()}
      </div>
    );
  }

  renderJobList() {
    let inRetryGroup = false;

    // job-list-pipeline is needed by the job components' styles
    return (
      <div className="job-list-pipeline">
        {this.state.build.jobs.map((job) => {
          // Don't even bother showing broken jobs
          if (job.state === 'broken') {
            return null;
          }

          if (job.type === "script") {
            // Figures out if we're inside a "retry-group" and comes up with
            // the neccessary class name.
            let retryGroupClassName;
            if (!inRetryGroup && job.retriedInJobUuid) { // Start of the group
              retryGroupClassName = "job-retry-group-first";
              inRetryGroup = true;
            } else if (inRetryGroup && job.retriedInJobUuid) { // Middle of the group
              retryGroupClassName = "job-retry-group-middle";
            } else if (inRetryGroup && !job.retriedInJobUuid) { // Ends of the group
              retryGroupClassName = "job-retry-group-last";
              inRetryGroup = false;
            }

            return (
              <JobComponent
                key={job.id}
                job={job}
                build={this.state.build}
                className={retryGroupClassName}
              />
            );
          } else if (job.type === "manual") {
            return <ManualJobSummary key={job.id} job={job} />;
          } else if (job.type === "trigger") {
            return <TriggerJobSummary key={job.id} job={job} />;
          } else if (job.type === "waiter") {
            return <WaiterJobSummary key={job.id} job={job} />;
          }
        })}
      </div>
    );
  }
}
